import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { doc, onSnapshot, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import SignaturePad from '../components/SignaturePad';

// Public, no-login page where a tour contact signs their Sports Tour Contract.
// Everything shown comes from the contracts/{token} doc (the terms are
// snapshotted onto it when sent), so the page never reads the tour itself.
// See onContractSendRequested / onContractUpdated in functions/index.js.

const TYPED_FONT = { fontFamily: '"Times New Roman", Times, serif', fontStyle: 'italic' };

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-[#F3F4F6] py-6 sm:py-10 px-4">
      <div className="max-w-3xl mx-auto space-y-4">
        <div className="bg-[#0D2240] rounded-[12px] px-6 py-5 flex items-center justify-between gap-4">
          <img src="/MST%20Logo%20White.png" alt="MST Sports Tours" className="h-10 w-auto" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          <p className="text-white font-bold text-[15px] text-right">Sports Tour Contract</p>
        </div>
        {children}
      </div>
    </div>
  );
}

function Notice({ title, body, tone = 'neutral', children }) {
  const ring = tone === 'good' ? 'bg-[#ECFDF5] text-[#1A7A4A]' : 'bg-[#F3F4F6] text-[#6B7280]';
  return (
    <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-8 text-center">
      {tone === 'good' && (
        <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 ${ring}`}>
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
      )}
      <p className="text-[17px] font-bold text-[#111827] mb-2">{title}</p>
      <p className="text-[13.5px] text-[#6B7280]">{body}</p>
      {children}
    </div>
  );
}

function ContractBody({ content }) {
  return (
    <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-5 sm:p-8 text-[13.5px] leading-relaxed text-[#374151]">
      <h1 className="text-[20px] font-bold text-[#0D2240]">{content.title}</h1>
      <p className="text-[14px] text-[#6B7280] mb-5">{content.subtitle}</p>
      {content.details?.length > 0 && (
        <dl className="bg-[#F9FAFB] rounded-[10px] border border-[#E5E7EB] px-4 py-3 mb-6 grid grid-cols-1 sm:grid-cols-[150px_1fr] gap-x-4 gap-y-1">
          {content.details.map(({ label, value }) => (
            <div key={label} className="contents">
              <dt className="text-[12px] font-bold text-[#6B7280] sm:py-0.5">{label}</dt>
              <dd className="text-[13px] text-[#111827] mb-1.5 sm:mb-0 sm:py-0.5">{value}</dd>
            </div>
          ))}
        </dl>
      )}
      {content.payments?.length > 0 && (
        <section className="mb-6">
          <h2 className="text-[15px] font-bold text-[#0D2240]">Payment schedule</h2>
          <p className="text-[12.5px] text-[#6B7280] mb-2">The payment schedule for this tour referred to in clause 4.</p>
          <table className="w-full border-collapse text-[13px]">
            <thead>
              <tr>{['Payment', 'Amount', 'Due date'].map((h) => <th key={h} className="border border-[#E5E7EB] bg-[#F9FAFB] px-2 py-1.5 text-left font-bold text-[#111827]">{h}</th>)}</tr>
            </thead>
            <tbody>
              {content.payments.map((p, i) => (
                <tr key={i}>
                  <td className="border border-[#E5E7EB] px-2 py-1.5">{p.label}</td>
                  <td className="border border-[#E5E7EB] px-2 py-1.5 whitespace-nowrap">{p.amount}</td>
                  <td className="border border-[#E5E7EB] px-2 py-1.5 whitespace-nowrap">{p.dueDate}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
      {content.sections.map((s, si) => (
        <section key={si} className="mb-5">
          {s.heading && <h2 className="text-[15px] font-bold text-[#0D2240] mb-2">{s.heading}</h2>}
          {s.blocks.map((b, bi) => {
            if (b.p) return <p key={bi} className="mb-2">{b.p}</p>;
            if (b.sub) return <p key={bi} className="mb-1.5 mt-3 font-bold text-[#111827]">{b.sub}</p>;
            if (b.ul) return (
              <ul key={bi} className="list-disc pl-5 mb-2 space-y-1">
                {b.ul.map((li, i) => <li key={i}>{li}</li>)}
              </ul>
            );
            if (b.table) return (
              <div key={bi} className="overflow-x-auto mb-3 -mx-1 px-1">
                <table className="w-full border-collapse text-[12.5px] min-w-[480px]">
                  <thead>
                    <tr>{b.table.head.map((h, i) => <th key={i} className="border border-[#E5E7EB] bg-[#F9FAFB] px-2 py-1.5 text-left font-bold text-[#111827] align-top">{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {b.table.rows.map((r, ri) => (
                      <tr key={ri}>{r.cells.map((c, ci) => <td key={ci} className="border border-[#E5E7EB] px-2 py-1.5 align-top">{c}</td>)}</tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
            return null;
          })}
        </section>
      ))}
      <p className="text-[11px] text-[#9CA3AF] mt-6 break-all">Terms version {content.version} · Fingerprint {content.hash}</p>
    </div>
  );
}

export default function SignContract() {
  const [params] = useSearchParams();
  const token = params.get('token');

  const [contract, setContract] = useState(null);
  const [state, setState] = useState('loading'); // loading | missing | ready
  const [name, setName] = useState('');
  const [position, setPosition] = useState('');
  const [organisation, setOrganisation] = useState('');
  const [mode, setMode] = useState('drawn'); // drawn | typed
  const [drawn, setDrawn] = useState(null);
  const [typed, setTyped] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) { setState('missing'); return undefined; }
    let first = true;
    return onSnapshot(doc(db, 'contracts', token), (snap) => {
      if (!snap.exists()) { setState('missing'); return; }
      const c = snap.data();
      setContract(c);
      setState('ready');
      if (first) {
        first = false;
        setName(c.recipientName || '');
        setOrganisation(c.organisation || '');
        if (c.status === 'sent' && !c.viewedAt) {
          updateDoc(snap.ref, { viewedAt: serverTimestamp() }).catch(() => {});
        }
      }
    }, () => setState('missing'));
  }, [token]);

  const signature = mode === 'drawn' ? drawn : typed.trim();
  const canSign = name.trim().length >= 2 && signature && (mode === 'drawn' || typed.trim().length >= 2) && agreed && !submitting;

  async function handleSign() {
    if (!canSign) return;
    setSubmitting(true);
    setError('');
    const signer = {
      name: name.trim().slice(0, 120),
      position: position.trim().slice(0, 120),
      organisation: organisation.trim().slice(0, 160),
      signatureType: mode,
      userAgent: (navigator.userAgent || '').slice(0, 400),
      agreed: true,
    };
    if (mode === 'drawn') signer.signatureImage = drawn;
    else signer.typedSignature = typed.trim().slice(0, 120);
    try {
      await updateDoc(doc(db, 'contracts', token), { status: 'signed', signer, signedAt: serverTimestamp() });
    } catch (err) {
      console.error(err);
      setError('Your signature could not be saved. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  }

  if (state === 'loading') return (
    <div className="min-h-screen bg-[#F3F4F6] flex items-center justify-center">
      <p className="text-[#9CA3AF] text-[14px]">Loading…</p>
    </div>
  );

  if (state === 'missing') return (
    <Shell><Notice title="Link not valid" body="This signing link is invalid or has expired. Please contact MST Sports Tours at info@mstsportstours.com." /></Shell>
  );

  const { status } = contract;

  if (status === 'void') return (
    <Shell><Notice title="This contract has been withdrawn" body="MST Sports Tours has replaced or cancelled this contract. If you've been sent a newer link, please use that one." /></Shell>
  );

  if (status === 'completed') return (
    <Shell>
      <Notice tone="good" title="Contract complete" body="This contract has been signed by you and countersigned by MST Sports Tours. A copy has been emailed to you.">
        {contract.downloadUrl && (
          <a href={contract.downloadUrl} target="_blank" rel="noreferrer"
            className="inline-block mt-5 px-5 py-2.5 bg-[#0D2240] hover:bg-[#1A4A8A] text-white font-semibold text-[13px] rounded-[8px] transition">
            Download signed contract (PDF)
          </a>
        )}
      </Notice>
    </Shell>
  );

  if (status === 'signed' || status === 'countersigning') return (
    <Shell>
      <Notice tone="good" title="Thank you — you've signed"
        body={`Signed by ${contract.signer?.name || 'you'}. MST Sports Tours will now countersign, and you'll receive the completed contract by email.`} />
    </Shell>
  );

  return (
    <Shell>
      <ContractBody content={contract.content} />

      <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-5 sm:p-8">
        <h2 className="text-[16px] font-bold text-[#0D2240] mb-1">Sign for the Customer (Group Leader)</h2>
        <p className="text-[13px] text-[#6B7280] mb-5">Please check your details, then add your signature.</p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
          {[
            ['Full name', name, setName, true],
            ['Position', position, setPosition, false],
            ['Organisation', organisation, setOrganisation, false],
          ].map(([label, value, set, required]) => (
            <label key={label} className="block">
              <span className="block text-[12px] font-semibold text-[#374151] mb-1">{label}{required && <span className="text-red-500"> *</span>}</span>
              <input value={value} onChange={(e) => set(e.target.value)} maxLength={label === 'Organisation' ? 160 : 120}
                placeholder={label === 'Position' ? 'e.g. Head Coach' : ''}
                className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1A4A8A]" />
            </label>
          ))}
        </div>

        <div className="flex gap-2 mb-3" role="tablist">
          {[['drawn', 'Draw signature'], ['typed', 'Type signature']].map(([id, label]) => (
            <button key={id} type="button" role="tab" aria-selected={mode === id} onClick={() => setMode(id)}
              className={`px-3.5 py-1.5 rounded-full text-[12.5px] font-semibold border transition ${
                mode === id ? 'bg-[#0D2240] text-white border-[#0D2240]' : 'bg-white text-[#374151] border-[#E5E7EB] hover:border-[#0D2240]'
              }`}>
              {label}
            </button>
          ))}
        </div>

        {mode === 'drawn' ? (
          <SignaturePad onChange={setDrawn} />
        ) : (
          <div>
            <input value={typed} onChange={(e) => setTyped(e.target.value)} maxLength={120} placeholder="Type your full name"
              className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1A4A8A]" />
            <div className="mt-2 h-[90px] rounded-[10px] border-2 border-dashed border-[#D1D5DB] flex items-center justify-center px-4 overflow-hidden">
              <span className="text-[34px] text-[#0D2240] truncate" style={TYPED_FONT}>{typed.trim() || ' '}</span>
            </div>
          </div>
        )}

        <label className="flex items-start gap-2.5 mt-5 cursor-pointer">
          <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)}
            className="mt-0.5 w-4 h-4 rounded border-[#D1D5DB] flex-shrink-0" />
          <span className="text-[13px] text-[#374151]">
            I have read and agree to this Sports Tour Contract on behalf of {organisation.trim() || 'my group'}, and I intend
            the signature above to be my legally binding electronic signature.
          </span>
        </label>

        {error && <p className="text-[13px] text-red-600 mt-3">{error}</p>}

        <button type="button" onClick={handleSign} disabled={!canSign}
          className="mt-5 w-full sm:w-auto px-6 py-3 bg-[#16A34A] hover:bg-[#15803D] text-white font-bold text-[14px] rounded-[9px] transition disabled:opacity-50 disabled:cursor-not-allowed">
          {submitting ? 'Signing…' : 'Sign contract'}
        </button>
        <p className="text-[11.5px] text-[#9CA3AF] mt-3">
          We record your name, the time you sign and your device type, and include them on the signed contract.
        </p>
      </div>
    </Shell>
  );
}
